import { observer } from 'mobx-react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Col, Container, Row, Spinner } from 'react-bootstrap';
import { storesContext } from '../../../stores/storesContext';
import PageTitle from '../../helpers/PageTitle';
import { errorToast } from '../../helpers/toasts/ToastUtils';
import CreateMonitoringModal from './monitoring/CreateMonitoringModal';
import MonitoringTable from './monitoring/MonitoringTable';
import BlockButtons from './monitoring_block/BlockButtons';
import BlockInfoCard from './monitoring_block/BlockInfoCard';
import CreatePackhouseAssessmentModal from './packhouse_assessment/CreatePackhouseAssessmentModal';
import PackhouseAssessmentTable from './packhouse_assessment/PackhouseAssessmentTable';
import PropertySelect from './PropertySelect';
import CreateSprayDataModal from './spray_data/CreateSprayDataModal';
import SprayDataTable from './spray_data/SprayDataTable';
import CreateTreeVigourMonitoringModal from './tree_vigour_monitoring/CreateTreeVigourMonitoringModal';
import TreeVigourMonitoringTable from './tree_vigour_monitoring/TreeVigourMonitoringTable';

const AlcmDataEntryPage = () => {
  const userStore = useContext(storesContext);
  const propertiesStore = userStore.propertiesStore;
  const alcmStore = userStore.alcmStore;
  const [selectedBlockId, setSelectedBlockId] = useState(null);

  const selectedBlock = useMemo(
    () => alcmStore.monitoringBlocks.find((mb) => mb.id === selectedBlockId),
    [alcmStore.monitoringBlocks, selectedBlockId]
  );

  useEffect(() => {
    if (!userStore.selectedDatabase || !propertiesStore.selectedProperty)
      return;

    alcmStore
      .fetchMonitoringBlocks(propertiesStore.selectedProperty.id)
      .catch(() => {
        errorToast('Failed to load blocks.');
      });
    alcmStore.fetchMetWatchStations().catch(() => {
      errorToast('Failed to load MetWatch stations.');
    });
  }, [alcmStore, propertiesStore.selectedProperty, userStore.selectedDatabase]);

  return (
    <Container id='page-container' className='mb-4'>
      <Row className='mb-3'>
        <Col>
          <PageTitle title={'ALCM Data Entry'} />
        </Col>
      </Row>

      {userStore.loading ? (
        <div className='text-center py-5'>
          <Spinner animation='border' variant='primary' />
          <p className='text-muted mt-2'>Loading properties...</p>
        </div>
      ) : (
        <>
          {/* Property Selection */}
          <Row className='mb-4 justify-content-center'>
            <Col>
              <PropertySelect />
            </Col>
          </Row>

          {/* Block Selection */}
          {alcmStore.loading || propertiesStore.loading ? (
            <div className='text-center py-4'>
              <Spinner animation='border' variant='primary' size='sm' />
              <p className='text-muted mt-2 mb-0'>Loading blocks...</p>
            </div>
          ) : (
            <>
              <Row className='mb-4 justify-content-center'>
                <Col md={10} lg={8}>
                  <BlockButtons
                    selectedBlockId={selectedBlockId}
                    onClick={(mb) => setSelectedBlockId(mb.id)}
                  />
                </Col>
              </Row>
              {alcmStore.monitoringBlocks.length === 0 &&
              propertiesStore.selectedProperty ? (
                <div className='text-center py-4'>
                  <p className='text-muted mb-0'>
                    This property has no monitoring blocks.
                  </p>
                </div>
              ) : null}

              {selectedBlock && (
                <Row>
                  <Col>
                    {/* Block Info Card */}
                    <Row className='mb-4 justify-content-center'>
                      <Col md={8} lg={6}>
                        <BlockInfoCard selectedBlock={selectedBlock} />
                      </Col>
                    </Row>
                    {/* Trap and Egg Monitoring Table */}
                    <Row className='mb-2'>
                      <Col>
                        <div className='d-flex justify-content-between mb-1'>
                          <h5>Trap & Egg Monitoring</h5>
                          <CreateMonitoringModal
                            monitoringBlock={selectedBlock}
                          />
                        </div>
                        <MonitoringTable selectedBlock={selectedBlock} />
                      </Col>
                    </Row>
                    {/* Tree Vigour Monitoring Table */}
                    <Row className='mb-2'>
                      <Col>
                        <div className='d-flex justify-content-between mb-1'>
                          <h5>Tree Vigour Monitoring</h5>
                          <CreateTreeVigourMonitoringModal
                            monitoringBlock={selectedBlock}
                          />
                        </div>
                        <TreeVigourMonitoringTable
                          selectedBlock={selectedBlock}
                        />
                      </Col>
                    </Row>
                    {/* Spray Data Table */}
                    <Row className='mb-2'>
                      <Col>
                        <div className='d-flex justify-content-between mb-1'>
                          <h5>Spray Data</h5>
                          <CreateSprayDataModal
                            monitoringBlock={selectedBlock}
                          />
                        </div>
                        <SprayDataTable selectedBlock={selectedBlock} />
                      </Col>
                    </Row>
                    {/* Packhouse Assessment Table */}
                    <Row className='mb-2'>
                      <Col>
                        <div className='d-flex justify-content-between mb-1'>
                          <h5>Packhouse Assessment</h5>
                          <CreatePackhouseAssessmentModal
                            monitoringBlock={selectedBlock}
                          />
                        </div>
                        <PackhouseAssessmentTable
                          selectedBlock={selectedBlock}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              )}
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default observer(AlcmDataEntryPage);

